<template>
  <DialogLayout
    v-model="isOpen"
    :title="title"
    width="500px"
    @input="handleDialogToggle"
  >
    <v-card>
      <v-card-text class="text-center pb-0">
        <div v-if="computedQrError" class="error-container">
          <v-alert type="error" dense class="mb-4">
            <div class="error-content">
              <p class="mb-2">{{ computedQrError.message }}</p>
            </div>
          </v-alert>

          <div class="d-flex justify-space-between">
            <p v-if="computedQrError.expiryTime" class="caption mb-0">
              <strong>Expires at:</strong> {{ computedQrError.expiryTime }}
            </p>
            <p v-if="computedQrError.timeRemaining" class="caption mb-0">
              <strong>Time remaining:</strong>
              {{ computedQrError.timeRemaining }}
            </p>
          </div>
        </div>

        <div v-else>
          <p class="mb-4">
            {{ description }}
          </p>
          <div v-if="qrUrl" class="qr-container">
            <canvas ref="qrCanvas" class="qr-canvas"></canvas>
          </div>
          <v-progress-circular
            v-else
            indeterminate
            color="primary"
            class="mb-4"
          />
          <p class="caption mt-4 grey--text">
            The QR code contains a secure token for temporary access for
            <br />
            <b>{{ tokenDuration }}</b>
          </p>
        </div>
      </v-card-text>
      <v-card-actions v-if="!computedQrError" class="justify-center pb-4">
        <v-btn v-if="qrUrl" color="primary" @click="copyToClipboard">
          <v-icon left>fas fa-copy</v-icon>
          Copy Link
        </v-btn>
      </v-card-actions>
    </v-card>
  </DialogLayout>
</template>

<script>
import DialogLayout from "@layout/DialogLayout.vue";
import { INITIATE_QR_SESSION } from "@components/authentication/graphql";
import { formatTime12hr } from "@tod-ui/helpers/datetime";
import QRCode from "qrcode";

export default {
  name: "QRCodeDialog",
  components: {
    DialogLayout,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "QR Code Access",
    },
    description: {
      type: String,
      default:
        "Scan this QR code with your mobile phone or tablet to access the feature.",
    },
    tokenDuration: {
      type: String,
      default: "90 minutes",
    },
    urlGenerator: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      qrUrl: null,
      qrToken: null,
      qrError: null,
      qrExpirationTime: null,
      isGenerating: false,
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
        if (!value) {
          this.resetQrState();
        }
      },
    },
    formattedExpiryTime() {
      if (!this.qrExpirationTime) return null;
      return formatTime12hr(this.qrExpirationTime);
    },
    timeRemainingDuration() {
      if (!this.qrExpirationTime) return null;
      // Always show in minutes — `durationInWords` rolls 60+ minutes up to
      // "1 hour" / "2 hours" which is harder to act on at a glance for a
      // QR session that's a small number of minutes from expiring.
      const expiresMs = new Date(this.qrExpirationTime).getTime();
      const remainingMs = expiresMs - Date.now();
      if (Number.isNaN(expiresMs) || remainingMs <= 0) return null;
      const minutes = Math.max(1, Math.round(remainingMs / 60000));
      return `${minutes} minute${minutes === 1 ? "" : "s"}`;
    },
    computedQrError() {
      if (!this.qrError) return null;

      return {
        ...this.qrError,
        expiryTime: this.formattedExpiryTime,
        timeRemaining: this.timeRemainingDuration,
      };
    },
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal) {
          this.generateQRCode();
        }
      },
      immediate: true,
    },
  },
  methods: {
    handleDialogToggle(value) {
      this.isOpen = value;
    },

    async generateQRCode() {
      try {
        this.isGenerating = true;
        this.qrError = null;
        this.qrToken = null;
        this.qrUrl = null;

        const result = await this.$apollo.mutate({
          mutation: INITIATE_QR_SESSION,
        });

        const qrToken = result.data?.initQrSession?.qrToken;

        if (!qrToken) {
          this.qrError = {
            message: "Failed to get QR token. Please try again.",
          };
          return;
        }

        this.qrToken = qrToken;
        await this.generateUrl();
      } catch (error) {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          const graphQLError = error.graphQLErrors[0];
          this.handleQrSessionError(graphQLError);
        } else {
          console.error("Error generating QR code:", error);
          this.qrError = {
            message: "Failed to generate QR code. Please try again.",
          };
        }
      } finally {
        this.isGenerating = false;
      }
    },

    handleQrSessionError(error) {
      const expirationMatch = error.message.match(
        /Expiration:\s*(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z)/
      );

      if (expirationMatch) {
        this.qrExpirationTime = expirationMatch[1];
      }

      this.qrError = {
        message:
          "A QR session was recently used. Please wait before creating a new one.",
      };
    },

    async generateUrl() {
      try {
        this.qrUrl = await this.urlGenerator(this.qrToken);

        await this.$nextTick();
        await this.renderQRCode();
      } catch (error) {
        console.error("Error generating URL:", error);
        this.qrError = {
          message: "Failed to generate URL. Please try again.",
        };
      }
    },

    async renderQRCode() {
      try {
        const canvas = this.$refs.qrCanvas;
        if (canvas && this.qrUrl) {
          await QRCode.toCanvas(canvas, this.qrUrl, {
            width: 256,
            margin: 2,
            color: {
              dark: "#000000",
              light: "#FFFFFF",
            },
          });
        }
      } catch (error) {
        console.error("Error rendering QR code:", error);
      }
    },

    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.qrUrl);
        this.$emit("copy-success", {
          message: "Link copied to clipboard",
          url: this.qrUrl,
        });
      } catch (error) {
        console.error("Error copying to clipboard:", error);
        const textArea = document.createElement("textarea");
        textArea.value = this.qrUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);

        this.$emit("copy-success", {
          message: "Link copied to clipboard",
          url: this.qrUrl,
        });
      }
    },

    resetQrState() {
      this.qrError = null;
      this.qrExpirationTime = null;
      this.qrUrl = null;
      this.qrToken = null;
      this.isGenerating = false;
    },
  },
};
</script>

<style scoped>
.qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
}

.qr-canvas {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.error-container {
  padding: 16px 0;
}

.error-content p {
  margin-bottom: 8px;
}

.error-content .caption {
  color: rgba(0, 0, 0, 0.6);
}
</style>
